// Shared behaviour for the campaign-* landing page sections.
document.addEventListener('DOMContentLoaded', function() {
  CampaignPage.init();
});

// Theme editor: re-init a section after it's re-rendered
document.addEventListener('shopify:section:load', function(event) {
  CampaignPage.init(event.target);
});

const CampaignPage = {
  selectors: {
    "sliders": { selector: "[data-cmp-slider]", type: "all", scope: "parent" },
    "faqs": { selector: "[data-cmp-faq]", type: "all", scope: "parent" },
    "bundles": { selector: "[data-cmp-bundle]", type: "all", scope: "parent" },
    "shops": { selector: "[data-cmp-shop]", type: "all", scope: "parent" },
    "features": { selector: "[data-cmp-feature]", type: "all", scope: "parent" },
    "recaps": { selector: "[data-cmp-recap]", type: "all", scope: "parent" },
    "tabSets": { selector: "[data-cmp-tabs]", type: "all", scope: "parent" },
  },

  // root is the re-rendered section in the theme editor, otherwise the whole page
  init: function(root) {
    this.parent = root || document;
    Utility.fetchEls(this.selectors, this);
    const { sliders, faqs, bundles, shops, features, recaps, tabSets } = this;

    sliders.forEach((el) => this.initSlider(el));
    faqs.forEach((el) => this.initFaq(el));
    bundles.forEach((el) => this.initBundle(el));
    shops.forEach((el) => this.initShop(el));
    features.forEach((el) => this.initProduct(el));
    recaps.forEach((el) => this.initRecap(el));
    tabSets.forEach((el) => this.initTabs(el));
    this.initJumpLinks();
  },

  // Links marked data-cmp-jump smooth scroll to an anchor on the page. One delegated listener covers
  // every section, including ones the theme editor re-renders later
  initJumpLinks: function() {
    if (this.isJumpLinksReady) {
      return;
    }
    this.isJumpLinksReady = true;

    document.addEventListener('click', (event) => {
      const link = event.target.closest('[data-cmp-jump]');
      if (!link) {
        return;
      }
      const hash = link.getAttribute('href') || '';
      if (!hash.startsWith('#') || hash.length < 2) {
        return;
      }
      const target = document.getElementById(decodeURIComponent(hash.slice(1)));
      if (!target) {
        return;
      }
      event.preventDefault();
      this.scrollToTarget(target);
      history.replaceState(null, '', hash);
    });
  },


  // Jump instead of animating for shoppers who ask for reduced motion
  scrollBehavior: function() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
  },

  // Horizontal scroll track with optional prev/next arrows and dots
  initSlider: function(slider) {
    if (slider.dataset.cmpReady) return;
    slider.dataset.cmpReady = 'true';

    // Sliders can be nested (bundle offers > product cards), so only pick up this slider's own parts
    const ownAll = (selector) => Array.from(slider.querySelectorAll(selector)).filter((el) => el.closest('[data-cmp-slider]') === slider);
    const own = (selector) => ownAll(selector)[0];
    const track = own('[data-cmp-track]');
    if (!track) return;
    // A slider can have more than one arrow pair (e.g. in the heading on desktop, under the cards on phones)
    const prevs = ownAll('[data-cmp-prev]');
    const nexts = ownAll('[data-cmp-next]');
    const dots = own('[data-cmp-dots]');
    const step = () => {
      // Grids that show several cards per view move by the whole columns that fit in view
      if (track.hasAttribute('data-cmp-page-step')) {
        const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
        const card = Array.from(track.children).find((el) => el.offsetWidth > 0);
        if (!card) return track.clientWidth + gap;
        const column = card.offsetWidth + gap;
        return Math.max(1, Math.floor((track.clientWidth + gap + 1) / column)) * column;
      }
      // Gender groups are display: contents, so measure the first visible card rather than the first child
      const first = Array.from(track.querySelectorAll(':scope > *, :scope > [data-cmp-gender-group] > *')).find((el) => el.offsetWidth > 0);
      const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
      return first ? first.offsetWidth + gap : track.clientWidth;
    };

    prevs.forEach((prev) => prev.addEventListener('click', () => track.scrollBy({ left: -step(), behavior: this.scrollBehavior() })));
    nexts.forEach((next) => next.addEventListener('click', () => track.scrollBy({ left: step(), behavior: this.scrollBehavior() })));
    if (track.hasAttribute('data-cmp-drag')) this.initDrag(track, step);

    // Arrows marked data-cmp-arrows hide when every card fits, and disable at either end
    const arrows = ownAll('[data-cmp-arrows]');
    if (!dots && !arrows.length) return;
    const renderDots = () => {
      // Count scroll steps rather than scrollWidth / clientWidth, which over-counts when slides are full width plus a gap
      const maxScroll = track.scrollWidth - track.clientWidth;
      const size = step();
      const pages = maxScroll > 1 && size > 0 ? Math.ceil(maxScroll / size - 0.05) + 1 : 1;
      if (arrows.length) {
        arrows.forEach((el) => (el.hidden = pages < 2));
        prevs.forEach((prev) => (prev.disabled = track.scrollLeft <= 1));
        nexts.forEach((next) => (next.disabled = track.scrollLeft >= maxScroll - 1));
      }
      if (!dots) return;
      if (dots.children.length !== pages) {
        dots.innerHTML = '<span></span>'.repeat(pages);
        dots.setAttribute('aria-hidden', 'true');
      }
      dots.hidden = pages < 2;
      const active = track.scrollLeft >= maxScroll - 1 ? pages - 1 : Math.min(pages - 1, Math.round(track.scrollLeft / size));
      Array.from(dots.children).forEach((dot, i) => dot.classList.toggle('is-active', i === active));
    };
    track.addEventListener('scroll', renderDots, { passive: true });
    window.addEventListener('resize', renderDots);
    renderDots();
  },

  // Mouse drag-to-slide for desktop; touch already scrolls natively.
  // A drag only starts after a few pixels so clicks on buttons and swatches still work.
  initDrag: function(track, step) {
    const THRESHOLD = 6;
    let startX = 0;
    let startScroll = 0;
    let pointerId = null;
    let dragging = false;
    let lastX = 0;
    let lastTime = 0;
    let velocity = 0; // px per ms, positive = moving content left (towards next)

    const finish = () => {
      if (pointerId === null) return;
      if (track.hasPointerCapture(pointerId)) track.releasePointerCapture(pointerId);
      pointerId = null;
      if (!dragging) return;

      // Snap to the nearest slide; a quick flick or a drag past a fifth of a slide moves one slide
      const size = step();
      const moved = track.scrollLeft - startScroll;
      const flick = Math.abs(velocity) > 0.4 && Date.now() - lastTime < 100;
      let index = Math.round(startScroll / size);
      if (flick) index += velocity > 0 ? 1 : -1;
      else if (Math.abs(moved) > size * 0.2) index += moved > 0 ? 1 : -1;
      track.scrollTo({ left: index * size, behavior: this.scrollBehavior() });

      const restore = () => {
        track.classList.remove('is-dragging');
        track.style.scrollSnapType = '';
      };
      if ('onscrollend' in window) {
        track.addEventListener('scrollend', restore, { once: true });
      } else {
        setTimeout(restore, 450);
      }
      // The click that follows pointerup is swallowed below; reset once it has fired
      setTimeout(() => { dragging = false; }, 0);
    };

    // Stop the click at the end of a drag from triggering a card or button
    track.addEventListener('click', (event) => {
      if (!dragging) return;
      event.preventDefault();
      event.stopPropagation();
    }, true);

    track.addEventListener('pointerdown', (event) => {
      if (event.pointerType !== 'mouse' || event.button !== 0) return;
      if (event.target.closest('select, input, textarea, label')) return;
      if (event.target.closest('[data-cmp-track]') !== track) return; // a nested slider handles its own drag
      if (track.scrollWidth <= track.clientWidth) return;
      pointerId = event.pointerId;
      startX = event.clientX;
      startScroll = track.scrollLeft;
      lastX = event.clientX;
      lastTime = Date.now();
      velocity = 0;
      dragging = false;
    });

    track.addEventListener('pointermove', (event) => {
      if (event.pointerId !== pointerId) return;
      const dx = event.clientX - startX;
      if (!dragging) {
        if (Math.abs(dx) < THRESHOLD) return;
        dragging = true;
        track.setPointerCapture(pointerId);
        track.classList.add('is-dragging');
        track.style.scrollSnapType = 'none';
      }
      track.scrollLeft = startScroll - dx;

      const now = Date.now();
      if (now > lastTime) velocity = (lastX - event.clientX) / (now - lastTime);
      lastX = event.clientX;
      lastTime = now;
    });

    track.addEventListener('pointerup', finish);
    track.addEventListener('pointercancel', finish);
    track.addEventListener('dragstart', (event) => event.preventDefault());
  },

  initFaq: function(faq) {
    if (faq.dataset.cmpReady) return;
    faq.dataset.cmpReady = 'true';

    faq.querySelectorAll('[data-cmp-faq-q]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const open = btn.getAttribute('aria-expanded') === 'true';
        btn.setAttribute('aria-expanded', String(!open));
        const panel = document.getElementById(btn.getAttribute('aria-controls'));
        if (panel) panel.hidden = open;
      });
    });
  },

  // Bundle builder: each [data-cmp-product] has its variants as JSON and option buttons.
  initBundle: function(bundle) {
    if (bundle.dataset.cmpReady) return;
    bundle.dataset.cmpReady = 'true';

    bundle.querySelectorAll('[data-cmp-product]').forEach((card) => this.initProductPicker(card));

    // Clicking anywhere on a main or add-on card marks it as the selected one of its role for its offer
    bundle.querySelectorAll('[data-cmp-offer]').forEach((offer) => {
      ['main', 'addon'].forEach((role) => {
        if (role === 'addon' && offer.dataset.addonMode === 'all') return;
        const selector = `[data-cmp-product][data-role="${role}"]`;
        offer.querySelectorAll(selector).forEach((card) => {
          card.addEventListener('click', () => {
            offer.querySelectorAll(selector).forEach((c) => c.classList.remove('is-active'));
            card.classList.add('is-active');
            if (card.cmpUpdate) card.cmpUpdate();
          });
        });
      });
    });

    // Men's/Women's toggle: swap to the paired product, keeping it selected if the pair was
    bundle.addEventListener('click', (event) => {
      const toggle = event.target.closest('[data-cmp-gender]');
      if (!toggle || !bundle.contains(toggle)) return;
      const current = toggle.closest('[data-cmp-product]');
      const group = toggle.closest('[data-cmp-gender-group]');
      const target = group && group.querySelector(`[data-cmp-product][data-gender="${toggle.dataset.cmpGender}"]`);
      if (!target || target === current) return;
      current.hidden = true;
      target.hidden = false;
      if (current.classList.contains('is-active')) {
        current.classList.remove('is-active');
        target.classList.add('is-active');
      }
      if (target.cmpUpdate) target.cmpUpdate();
      const focusBtn = target.querySelector(`[data-cmp-gender="${toggle.dataset.cmpGender}"]`);
      if (focusBtn) focusBtn.focus();
    });

    bundle.addEventListener('click', (event) => {
      const btn = event.target.closest('[data-cmp-add]');
      if (!btn || !bundle.contains(btn)) return;
      event.preventDefault();

      const offer = btn.closest('[data-cmp-offer]');
      const targets = btn.dataset.cmpAdd.split(',');
      const cards = [];
      targets.forEach((target) => {
        if (target === 'self') cards.push(btn.closest('[data-cmp-product]'));
        if (target === 'main') cards.push(offer.querySelector('[data-cmp-product][data-role="main"].is-active') || offer.querySelector('[data-cmp-product][data-role="main"]:not([hidden])'));
        if (target === 'addon') {
          if (offer.dataset.addonMode === 'all') {
            cards.push(...offer.querySelectorAll('[data-cmp-product][data-role="addon"]:not([hidden])'));
          } else {
            cards.push(offer.querySelector('[data-cmp-product][data-role="addon"].is-active') || offer.querySelector('[data-cmp-product][data-role="addon"]:not([hidden])'));
          }
        }
      });

      const items = cards
        .filter(Boolean)
        .map((card) => this.cartItem(card))
        .filter((item) => item.id);

      if (items.length) this.addToCart(btn, items);
    });

    // Footer summary prices follow the selected main and add-on(s)
    bundle.addEventListener('cmp:variant-change', (event) => {
      const offer = event.target.closest('[data-cmp-offer]');
      if (offer) this.renderSummary(offer);
    });
    bundle.querySelectorAll('[data-cmp-offer]').forEach((offer) => this.renderSummary(offer));

    // Offer footer buttons sit outside the cards: any card's update rechecks them against the active cards
    bundle.addEventListener('cmp:add-done', (event) => {
      if (event.target.closest('[data-cmp-product]')) return;
      const card = event.target.closest('[data-cmp-offer]')?.querySelector('[data-cmp-product]');
      if (card && card.cmpUpdate) card.cmpUpdate();
    });
  },

  // Standalone product cards (promo grid): variant pickers plus an add-to-cart button per card
  initShop: function(root) {
    if (root.dataset.cmpReady) return;
    root.dataset.cmpReady = 'true';

    root.querySelectorAll('[data-cmp-product]').forEach((card) => this.initProductPicker(card));

    root.addEventListener('click', (event) => {
      const btn = event.target.closest('[data-cmp-add]');
      if (!btn || !root.contains(btn)) return;
      event.preventDefault();
      const card = btn.closest('[data-cmp-product]');
      const item = card && this.cartItem(card);
      if (item && item.id) this.addToCart(btn, [item]);
    });
  },

  initProductPicker: function(card) {
    const dataEl = card.querySelector('[data-cmp-variants]');
    if (!dataEl) return;
    const variants = JSON.parse(dataEl.textContent);
    // Copy the options: mutating the variant's own array would corrupt the lookup below
    const selected = [...((variants.find((v) => v.id === Number(card.dataset.variantId)) || variants[0] || {}).options || [])];

    // Whether a value has an available variant with the options chosen before it (option order, like Shopify themes):
    // the first option is judged on its own, so Women's is not crossed out just because no Women's XXL exists
    const isAvailable = (index, value) =>
      variants.some((v) => v.available && v.options[index] === value && v.options.every((opt, i) => i >= index || opt === selected[i]));
    // Values with no stock in any combination can't be picked at all
    const isSoldOutEverywhere = (index, value) => !variants.some((v) => v.available && v.options[index] === value);

    const update = () => {
      const variant = variants.find((v) => v.options.every((opt, i) => opt === selected[i]));
      card.querySelectorAll('[data-cmp-option]').forEach((btn) => {
        const index = Number(btn.dataset.index);
        btn.classList.toggle('is-selected', btn.dataset.value === selected[index]);
        btn.setAttribute('aria-pressed', String(btn.dataset.value === selected[index]));
        const soldOut = isSoldOutEverywhere(index, btn.dataset.value);
        btn.classList.toggle('is-unavailable', soldOut || !isAvailable(index, btn.dataset.value));
        btn.disabled = soldOut && btn.dataset.value !== selected[index];
      });
      card.querySelectorAll('[data-cmp-option-select]').forEach((select) => {
        const index = Number(select.dataset.index);
        select.value = selected[index];
        Array.from(select.options).forEach((option) => {
          const available = isAvailable(index, option.value);
          option.textContent = available || !select.dataset.soldOut ? option.dataset.label : `${option.dataset.label} – ${select.dataset.soldOut}`;
          option.disabled = isSoldOutEverywhere(index, option.value) && option.value !== selected[index];
        });
      });
      card.querySelectorAll('[data-cmp-option-label]').forEach((label) => {
        label.textContent = selected[Number(label.dataset.index)] || '';
      });

      const buttons = card.closest('[data-cmp-offer]')?.querySelectorAll('[data-cmp-add]') || [];
      if (variant) {
        card.dataset.variantId = variant.id;
        card.cmpVariant = variant;
        this.renderCardPrice(card, variant);
        if (variant.image) {
          const img = card.querySelector('[data-cmp-image]') || card.querySelector('.cmp-picker__media img, .cmp-card__media img');
          if (img) {
            img.src = variant.image;
            img.removeAttribute('srcset');
          }
        }
      }
      const soldOut = !variant || !variant.available;
      card.classList.toggle('is-sold-out', soldOut);
      card.dispatchEvent(new CustomEvent('cmp:variant-change', { detail: { variant }, bubbles: true }));
      card.querySelectorAll('[data-cmp-add]').forEach((btn) => (btn.disabled = soldOut));
      buttons.forEach((btn) => {
        if (btn.closest('[data-cmp-product]')) return;
        const offerCards = btn.closest('[data-cmp-offer]').querySelectorAll('[data-cmp-product]');
        btn.disabled = Array.from(offerCards).some((c) => c.classList.contains('is-sold-out') && c.classList.contains('is-active'));
      });
    };

    // Picking a value with no in-stock variant for the current selection (e.g. Women's while XXL is selected) keeps the
    // picked value and moves the other options to the in-stock variant that keeps the most current choices
    const selectValue = (index, value) => {
      selected[index] = value;
      const exactMatch = variants.some((v) => v.available && v.options.every((opt, i) => opt === selected[i]));
      if (exactMatch) {
        return;
      }
      const candidates = variants.filter((v) => v.available && v.options[index] === value);
      if (!candidates.length) {
        return;
      }
      const sharedChoices = (v) => v.options.filter((opt, i) => opt === selected[i]).length;
      const closest = candidates.reduce((best, v) => (sharedChoices(v) > sharedChoices(best) ? v : best));
      closest.options.forEach((opt, i) => {
        selected[i] = opt;
      });
    };

    card.querySelectorAll('[data-cmp-option]').forEach((btn) => {
      btn.addEventListener('click', () => {
        selectValue(Number(btn.dataset.index), btn.dataset.value);
        update();
      });
    });
    card.querySelectorAll('[data-cmp-option-select]').forEach((select) => {
      select.addEventListener('change', () => {
        selectValue(Number(select.dataset.index), select.value);
        update();
      });
    });
    card.addEventListener('cmp:add-done', update);
    card.cmpUpdate = update;
    update();
  },

  // Single product feature: gallery thumbs + zoom, variant cards, quantity and add to cart
  initProduct: function(root) {
    if (root.dataset.cmpReady) return;
    root.dataset.cmpReady = 'true';

    const slides = root.querySelectorAll('[data-cmp-slide]');
    const thumbs = root.querySelectorAll('[data-cmp-thumb]');
    const showMedia = (mediaId) => {
      if (!mediaId || !root.querySelector(`[data-cmp-slide][data-media-id="${mediaId}"]`)) return;
      slides.forEach((slide) => (slide.hidden = slide.dataset.mediaId !== mediaId));
      thumbs.forEach((thumb) => {
        const active = thumb.dataset.mediaId === mediaId;
        thumb.classList.toggle('is-active', active);
        if (active) thumb.setAttribute('aria-current', 'true');
        else thumb.removeAttribute('aria-current');
      });
    };
    thumbs.forEach((thumb) => thumb.addEventListener('click', () => showMedia(thumb.dataset.mediaId)));

    const dialog = root.querySelector('[data-cmp-zoom-dialog]');
    const zoomBtn = root.querySelector('[data-cmp-zoom]');
    if (dialog && zoomBtn && typeof dialog.showModal === 'function') {
      zoomBtn.addEventListener('click', () => {
        const current = Array.from(slides).find((slide) => !slide.hidden);
        if (!current || !current.dataset.zoomSrc) return;
        const zoomImg = dialog.querySelector('[data-cmp-zoom-img]');
        zoomImg.src = current.dataset.zoomSrc;
        zoomImg.alt = current.querySelector('img')?.alt || '';
        dialog.showModal();
      });
      dialog.querySelector('[data-cmp-zoom-close]').addEventListener('click', () => dialog.close());
      dialog.addEventListener('click', (event) => {
        if (event.target === dialog) dialog.close();
      });
    }

    const atc = root.querySelector('[data-cmp-product-atc]');
    if (!atc) return;
    const price = root.querySelector('[data-cmp-price]');
    const meta = root.querySelector('[data-cmp-price-meta]');
    const ships = root.querySelector('[data-cmp-ships]');
    const shipsText = root.querySelector('[data-cmp-ships-text]');
    const purchase = root.querySelector('[data-cmp-purchase]');
    const restricted = root.querySelector('[data-cmp-restricted]');
    const status = root.querySelector('[data-cmp-pack-status]');
    let shipsValue = ships && !ships.hidden ? shipsText.textContent.replace(/^Ships in /, '') : '';
    // Last applied variant info; null when the selected options match no variant
    let currentInfo;

    // info: { id, price, compare, meta, available, restricted, ships, mediaId } as strings, from data attributes or JSON
    const applyVariant = (info) => {
      currentInfo = info;
      const available = String(info.available) === 'true';
      const isRestricted = String(info.restricted) === 'true';
      price.innerHTML = '';
      const label = (text) => {
        const span = document.createElement('span');
        span.className = 'visually-hidden';
        span.textContent = text;
        return span;
      };
      if (info.compare) {
        const was = document.createElement('s');
        was.textContent = info.compare;
        price.append(label(price.dataset.labelRegular), was, label(price.dataset.labelSale));
      }
      price.append(info.price);
      meta.textContent = info.meta || '';
      shipsValue = info.ships || '';
      if (ships) {
        ships.hidden = !shipsValue || !available;
        shipsText.textContent = `Ships in ${shipsValue}`;
      }
      atc.dataset.variantId = info.id;
      atc.disabled = !available;
      atc.textContent = available ? atc.dataset.labelAdd : atc.dataset.labelSoldOut;
      purchase.hidden = isRestricted;
      restricted.hidden = !isRestricted;
      if (status) status.textContent = isRestricted ? restricted.textContent.trim() : '';
      showMedia(info.mediaId ? String(info.mediaId) : '');
    };

    // Pack cards (one radio per variant)
    root.querySelectorAll('[data-cmp-pack]').forEach((input) => {
      input.addEventListener('change', () => {
        root.querySelectorAll('[data-cmp-pack]').forEach((other) => other.closest('label').classList.toggle('is-selected', other === input));
        applyVariant({ ...input.dataset, id: input.value });
      });
    });

    // Option pickers (shape pills, colour swatches, size dropdown)
    const options = root.querySelector('[data-cmp-product]');
    const infoEl = root.querySelector('[data-cmp-variant-info]');
    if (options && infoEl) {
      const info = JSON.parse(infoEl.textContent);
      options.addEventListener('cmp:variant-change', (event) => {
        const variant = event.detail.variant;
        if (!variant) {
          currentInfo = null;
          atc.disabled = true;
          atc.textContent = atc.dataset.labelSoldOut;
          return;
        }
        if (info[variant.id]) applyVariant({ id: variant.id, ...info[variant.id] });
      });
      this.initProductPicker(options);
    }

    const qtyValue = root.querySelector('[data-cmp-qty-value]');
    const minus = root.querySelector('[data-cmp-qty="-1"]');
    let qty = 1;
    root.querySelectorAll('[data-cmp-qty]').forEach((btn) => {
      btn.addEventListener('click', () => {
        qty = Math.max(1, qty + Number(btn.dataset.cmpQty));
        qtyValue.textContent = qty;
        minus.disabled = qty === 1;
      });
    });

    atc.addEventListener('click', () => {
      const item = { id: Number(atc.dataset.variantId), quantity: qty, handle: atc.dataset.handle };
      if (shipsValue) item.properties = { 'Shipping Delay': shipsValue };
      this.addToCart(atc, [item]);
    });

    // After add to cart, put the button back to match the variant selected now
    atc.addEventListener('cmp:add-done', (event) => {
      event.stopPropagation();
      if (currentInfo) {
        applyVariant(currentInfo);
      } else if (currentInfo === null) {
        atc.disabled = true;
        atc.textContent = atc.dataset.labelSoldOut;
      }
    });
  },

  // Cart line for a card's selected variant, with the same Shipping Delay property the product feature sends
  cartItem: function(card) {
    const item = { id: Number(card.dataset.variantId), quantity: 1, handle: card.dataset.handle };
    const ships = card.cmpVariant && card.cmpVariant.ships;
    if (ships) item.properties = { 'Shipping Delay': ships };
    return item;
  },

  // Amounts as the Liquid prints them: money_without_trailing_zeros, currency code removed
  formatPrice: function(cents) {
    return Utility.formatLocalizedMoney(cents);
  },

  // Display price for an add-on discount; floors like the Liquid `times | divided_by` it replaces
  discounted: function(cents, discount) {
    return Math.floor((cents * (100 - discount)) / 100);
  },

  // Card price for the selected variant: a display-only add-on discount, otherwise the variant's compare-at price
  renderCardPrice: function(card, variant) {
    const el = card.querySelector('[data-cmp-card-price]');
    if (!el || typeof variant.price !== 'number') return;
    const discount = Number(el.dataset.discount) || 0;
    const was = discount > 0 ? variant.price : variant.compare > variant.price ? variant.compare : 0;
    const now = discount > 0 ? this.discounted(variant.price, discount) : variant.price;
    // Promo grid "% off" badge, rounded like the Liquid that first renders it
    const badge = card.querySelector('[data-cmp-badge]');
    if (badge) {
      const pct = discount > 0 || !was ? 0 : Math.round(((was - now) * 100) / was);
      badge.hidden = pct <= 0;
      if (pct > 0) badge.textContent = `${pct}% off`;
    }
    const label = (text) => {
      const span = document.createElement('span');
      span.className = 'visually-hidden';
      span.textContent = text;
      return span;
    };
    el.innerHTML = '';
    if (was) {
      const s = document.createElement('s');
      s.textContent = this.formatPrice(was);
      const sale = document.createElement('span');
      sale.className = 'cmp-price__sale';
      // Free gifts read "Free" (data-label-free) rather than a zero price
      sale.textContent = now === 0 && el.dataset.labelFree ? el.dataset.labelFree : this.formatPrice(now);
      el.append(label(el.dataset.labelRegular), s, label(el.dataset.labelSale), sale);
    } else {
      const span = document.createElement('span');
      span.textContent = this.formatPrice(now);
      el.append(span);
    }
  },

  // "Main + add-on (40% off)" from the selected main and the add-on(s) that the footer button adds
  renderSummary: function(offer) {
    const summary = offer.querySelector('[data-cmp-summary]');
    if (!summary) return;
    const pick = (role) => offer.querySelector(`[data-cmp-product][data-role="${role}"].is-active`) || offer.querySelector(`[data-cmp-product][data-role="${role}"]:not([hidden])`);
    const main = pick('main');
    const addons = offer.dataset.addonMode === 'all'
      ? Array.from(offer.querySelectorAll('[data-cmp-product][data-role="addon"]:not([hidden])'))
      : [pick('addon')].filter(Boolean);
    if (!main || !main.cmpVariant || !addons.length || addons.some((card) => !card.cmpVariant)) return;
    const discount = Number(summary.dataset.discount) || 0;
    const addonTotal = addons.reduce((sum, card) => sum + this.discounted(card.cmpVariant.price, discount), 0);
    // Holiday style: "$312 value - only $300" from the translated template Liquid left on the element
    const summaryTemplate = summary.dataset.summaryTemplate;
    if (summaryTemplate) {
      const addonFullPrice = addons.reduce((sum, card) => sum + card.cmpVariant.price, 0);
      summary.textContent = summaryTemplate
        .replace('{{ value }}', this.formatPrice(main.cmpVariant.price + addonFullPrice))
        .replace('{{ total }}', this.formatPrice(main.cmpVariant.price + addonTotal));
      return;
    }
    summary.textContent = `${this.formatPrice(main.cmpVariant.price)} + ${this.formatPrice(addonTotal)} (${discount}% off)`;
  },

  // Smooth scroll to an element, leaving room for a fixed or sticky site header
  scrollToTarget: function(target) {
    const header = document.querySelector('header, .header, [data-header]');
    const headerBottom = header ? Math.min(Math.max(header.getBoundingClientRect().bottom, 0), 200) : 0;
    const top = target.getBoundingClientRect().top + window.scrollY - headerBottom - 16;
    window.scrollTo({ top: Math.max(top, 0), behavior: this.scrollBehavior() });
  },

  // Product grid tabs: one panel per collection. The "Shop all" links follow the selected tab.
  initTabs: function(root) {
    if (root.dataset.cmpReady) {
      return;
    }
    root.dataset.cmpReady = 'true';

    const tabs = Array.from(root.querySelectorAll('[data-cmp-tab]'));
    const links = root.querySelectorAll('[data-cmp-tabs-link]');
    const heading = root.querySelector('[data-cmp-tabs-heading]');
    const description = root.querySelector('[data-cmp-tabs-description]');

    const select = (tab, moveFocus, updateUrl = true) => {
      tabs.forEach((other) => {
        const isSelected = other === tab;
        other.setAttribute('aria-selected', String(isSelected));
        other.tabIndex = isSelected ? 0 : -1;
        const panel = document.getElementById(other.getAttribute('aria-controls'));
        if (panel) {
          panel.hidden = !isSelected;
        }
      });
      links.forEach((link) => {
        link.hidden = !tab.dataset.linkUrl;
        link.href = tab.dataset.linkUrl || '#';
        link.textContent = tab.dataset.linkLabel || '';
      });
      // "Use the selected tab's collection" on: the heading is escaped title text with an <em> Liquid added for the accent
      if (heading && tab.dataset.heading !== undefined) {
        heading.innerHTML = tab.dataset.heading;
      }
      if (description && tab.dataset.description !== undefined) {
        // The description keeps the collection's HTML entities (e.g. &amp;); a textarea decodes them as plain text,
        // so it reads like the first render
        const decoder = document.createElement('textarea');
        decoder.innerHTML = tab.dataset.description;
        description.textContent = decoder.value;
        description.hidden = !decoder.value;
      }
      // A slider measured while hidden has no width, so refresh its dots and arrows now it shows
      const panel = document.getElementById(tab.getAttribute('aria-controls'));
      const track = panel && panel.querySelector('[data-cmp-track]');
      if (track) {
        track.scrollLeft = 0;
        track.dispatchEvent(new Event('scroll'));
      }
      if (moveFocus) {
        tab.focus();
      }
      // Keep the selected collection in the URL (?collection=handle) so the tab can be shared or reloaded.
      // replaceState avoids one history entry per tab click
      if (updateUrl && tab.dataset.collectionHandle) {
        const url = new URL(window.location.href);
        url.searchParams.set('collection', tab.dataset.collectionHandle);
        window.history.replaceState(window.history.state, '', url);
      }
    };

    // Open the tab named in the URL, if this grid has it
    const requestedHandle = new URLSearchParams(window.location.search).get('collection');
    const requestedTab = requestedHandle && tabs.find((tab) => tab.dataset.collectionHandle === requestedHandle);
    if (requestedTab) {
      select(requestedTab, false, false);
      // On phones the tab row scrolls sideways; bring the tab into view without scrolling the page
      const tabList = requestedTab.parentElement;
      const tabListPadding = parseFloat(getComputedStyle(tabList).paddingLeft) || 0;
      tabList.scrollLeft += requestedTab.getBoundingClientRect().left - tabList.getBoundingClientRect().left - tabListPadding;
    }

    tabs.forEach((tab, index) => {
      tab.addEventListener('click', () => select(tab, false));
      // Arrow keys, Home and End move between tabs (WAI-ARIA tabs pattern)
      tab.addEventListener('keydown', (event) => {
        const keyTargets = {
          ArrowRight: tabs[(index + 1) % tabs.length],
          ArrowLeft: tabs[(index - 1 + tabs.length) % tabs.length],
          Home: tabs[0],
          End: tabs[tabs.length - 1]
        };
        const target = keyTargets[event.key];
        if (!target) {
          return;
        }
        event.preventDefault();
        select(target, true);
      });
    });
  },

  // Closing recap: tiles that scroll back to each deal (data-cmp-jump, see initJumpLinks), back to top
  initRecap: function(root) {
    if (root.dataset.cmpReady) return;
    root.dataset.cmpReady = 'true';

    const back = root.querySelector('[data-cmp-back-to-top]');
    if (back) {
      back.addEventListener('click', (event) => {
        event.preventDefault();
        const nav = document.querySelector('.cmp-nav');
        if (nav) this.scrollToTarget(nav);
        else window.scrollTo({ top: 0, behavior: this.scrollBehavior() });
      });
    }
  },

  // One shared live region for add to cart results
  announce: function(message) {
    if (!this.status) {
      this.status = document.createElement('p');
      this.status.className = 'visually-hidden';
      this.status.setAttribute('role', 'status');
      document.body.append(this.status);
    }
    this.status.textContent = '';
    setTimeout(() => (this.status.textContent = message), 100);
  },

  addToCart: async function(btn, items) {
    // Variant updates can re-enable the button mid-request, so guard against a second add
    if (btn.dataset.cmpPending) return;
    btn.dataset.cmpPending = 'true';
    const original = btn.textContent;
    btn.disabled = true;
    btn.textContent = btn.dataset.addingText || 'Adding...';

    let added = false;
    try {
      const response = await fetch(window.Shopify.routes.root + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: items.map(({ id, quantity, properties }) => (properties ? { id, quantity, properties } : { id, quantity })) }),
      });
      if (!response.ok) throw new Error('Add to cart failed');
      added = true;
    } catch (error) {
      console.error('CampaignPage: error adding to cart', error);
      btn.textContent = btn.dataset.errorText || 'Error';
      this.announce(btn.textContent);
    }

    if (added) {
      // The items are in the cart now; a drawer refresh failure shouldn't report the add as failed
      try {
        if (typeof Cart !== 'undefined') {
          for (const item of items) {
            await Cart.addItemToSidebarCart(String(item.id), item.quantity, null, item.handle, true, String(item.id));
          }
          await Cart.updateCart();
        }
      } catch (error) {
        console.error('CampaignPage: error refreshing the cart drawer', error);
      }
      btn.textContent = btn.dataset.addedText || 'Added';
      this.announce(btn.textContent);
    }

    // The variant may have changed (e.g. to a sold-out one) while the label showed, so let the owner recheck the button
    setTimeout(() => {
      btn.textContent = original;
      btn.disabled = false;
      delete btn.dataset.cmpPending;
      btn.dispatchEvent(new CustomEvent('cmp:add-done', { bubbles: true }));
    }, 2000);
  },
};
